import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, todayIso } from './dates';
import { classLabel } from './contexts';
import { type Db, must, useDb } from './db';

export type LessonFormat = Database['public']['Enums']['lesson_format'];
export type LessonStatus = Database['public']['Enums']['lesson_status'];

export interface PersonName {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
}

export interface LessonItem {
  id: string;
  date: string;
  period: number;
  startsAt: string;
  endsAt: string;
  format: LessonFormat;
  status: LessonStatus;
  topic: string | null;
  /** what the teacher asked to bring */
  bring: string | null;
  /** «контрольна»: the teacher marked it as a test */
  isTest: boolean;
  room: string | null;
  termId: string;
  courseId: string;
  subject: string;
  groupLabel: string | null;
  classId: string;
  classLabel: string;
  grade: number;
  teachers: PersonName[];
  substitute: PersonName | null;
}

const LESSON_SELECT = `
  id, date, period, starts_at, ends_at, format, status, topic, bring, is_test, room, term_id, course_id,
  substitute:school_members!lessons_school_id_substitute_member_id_fkey(id, first_name, last_name, middle_name),
  course:courses!lessons_school_id_course_id_fkey(
    id, group_label,
    subject:subjects!courses_school_id_subject_id_fkey(name),
    class:classes!courses_school_id_class_id_fkey(id, grade, letter),
    teachers:course_teachers(
      teacher:school_members!course_teachers_school_id_teacher_member_id_fkey(id, first_name, last_name, middle_name)
    )
  )
`;

const person = (p: {
  id: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
}): PersonName => ({
  id: p.id,
  firstName: p.first_name,
  lastName: p.last_name,
  middleName: p.middle_name,
});

/** «Щербак Д. А.» */
export const shortName = (p: PersonName) =>
  `${p.lastName} ${p.firstName.slice(0, 1)}.${p.middleName ? ` ${p.middleName.slice(0, 1)}.` : ''}`;

const selectLessons = (db: Db) => db.from('lessons').select(LESSON_SELECT);
type LessonRow = NonNullable<Awaited<ReturnType<typeof selectLessons>>['data']>[number];

function toLessonItem(l: LessonRow): LessonItem {
  return {
    id: l.id,
    date: l.date,
    period: l.period,
    startsAt: l.starts_at,
    endsAt: l.ends_at,
    format: l.format,
    status: l.status,
    topic: l.topic,
    bring: l.bring,
    isTest: l.is_test,
    room: l.room,
    termId: l.term_id,
    courseId: l.course_id,
    subject: l.course.subject.name,
    groupLabel: l.course.group_label,
    classId: l.course.class.id,
    classLabel: classLabel(l.course.class),
    grade: l.course.class.grade,
    teachers: l.course.teachers.map((t) => person(t.teacher)),
    substitute: l.substitute ? person(l.substitute) : null,
  };
}

export async function fetchLessons(
  db: Db,
  params: { schoolId: string; from: string; to: string; courseIds?: ReadonlyArray<string> },
): Promise<LessonItem[]> {
  let query = db
    .from('lessons')
    .select(LESSON_SELECT)
    .eq('school_id', params.schoolId)
    .gte('date', params.from)
    .lte('date', params.to)
    .order('date')
    .order('period');
  if (params.courseIds) query = query.in('course_id', [...params.courseIds]);
  return must(await query).map(toLessonItem);
}

/** One lesson, if one may see it. */
export async function fetchLessonById(db: Db, id: string): Promise<LessonItem | null> {
  const { data, error } = await selectLessons(db).eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? toLessonItem(data) : null;
}

/** Who the schedule is for: a student's courses, a teacher's courses, a class, or everything. */
export type ScheduleScope =
  | { kind: 'student'; studentId: string }
  | { kind: 'teacher'; memberId: string }
  | { kind: 'class'; classId: string }
  | { kind: 'school' };

export async function fetchScopeCourseIds(
  db: Db,
  scope: ScheduleScope,
): Promise<string[] | undefined> {
  switch (scope.kind) {
    case 'student':
      return must(
        await db
          .from('course_students')
          .select('course_id')
          .eq('student_member_id', scope.studentId),
      ).map((r) => r.course_id);
    case 'teacher':
      return must(
        await db
          .from('course_teachers')
          .select('course_id')
          .eq('teacher_member_id', scope.memberId),
      ).map((r) => r.course_id);
    case 'class':
      return must(await db.from('courses').select('id').eq('class_id', scope.classId)).map(
        (r) => r.id,
      );
    case 'school':
      return undefined;
  }
}

export function useLessons(schoolId: string, scope: ScheduleScope, from: string, to: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['lessons', schoolId, scope, from, to],
    queryFn: async () => {
      const courseIds = await fetchScopeCourseIds(db, scope);
      const lessons = await fetchLessons(db, {
        schoolId,
        from,
        to,
        ...(courseIds && { courseIds }),
      });
      if (scope.kind !== 'teacher') return lessons;
      // a teacher also sees the lessons they substitute in other classes
      const substitutes = await fetchLessons(db, { schoolId, from, to }).then((all) =>
        all.filter((l) => l.substitute?.id === scope.memberId),
      );
      return [...lessons, ...substitutes].sort(
        (a, b) => a.date.localeCompare(b.date) || a.period - b.period,
      );
    },
  });
}

export function useBellPeriods(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['bell-periods', schoolId],
    staleTime: 60 * 60_000,
    queryFn: async () =>
      must(
        await db
          .from('bell_periods')
          .select('number, starts_at, ends_at')
          .eq('school_id', schoolId)
          .order('number'),
      ),
  });
}

export function useClasses(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['classes', schoolId],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      must(
        await db
          .from('classes')
          .select('id, grade, letter, homeroom_member_id, shelter_id')
          .eq('school_id', schoolId)
          .order('grade')
          .order('letter'),
      ).map((c) => ({
        id: c.id,
        grade: c.grade,
        label: classLabel(c),
        homeroomId: c.homeroom_member_id,
        shelterId: c.shelter_id,
      })),
  });
}

/** Lessons of today, or of the next day that has any (weekends, holidays). */
export function useUpcomingDay(schoolId: string, scope: ScheduleScope) {
  const today = todayIso();
  const lessons = useLessons(schoolId, scope, today, addDays(today, 10));
  const first = lessons.data?.[0]?.date;
  return {
    isPending: lessons.isPending,
    isFetching: lessons.isFetching,
    refetch: lessons.refetch,
    date: first,
    isToday: first === today,
    lessons: (lessons.data ?? []).filter((l) => l.date === first),
  };
}

export interface ClassNow {
  classId: string;
  classLabel: string;
  grade: number;
  /** the lesson going on now, else the next one today, else none */
  lesson: LessonItem | null;
  live: boolean;
}

/** What every class of the school is doing right now: one line per class, by grade. */
export function classesNow(lessons: ReadonlyArray<LessonItem>, now = Date.now()): ClassNow[] {
  const byClass = new Map<string, LessonItem[]>();
  for (const l of lessons) {
    if (l.status !== 'scheduled') continue;
    byClass.set(l.classId, [...(byClass.get(l.classId) ?? []), l]);
  }
  return [...byClass.values()]
    .map((list): ClassNow => {
      const sorted = [...list].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      const live = sorted.find((l) => Date.parse(l.startsAt) <= now && now < Date.parse(l.endsAt));
      const next = sorted.find((l) => Date.parse(l.startsAt) > now);
      const first = sorted[0] as LessonItem;
      return {
        classId: first.classId,
        classLabel: first.classLabel,
        grade: first.grade,
        lesson: live ?? next ?? null,
        live: live !== undefined,
      };
    })
    .sort((a, b) => a.grade - b.grade || a.classLabel.localeCompare(b.classLabel, 'uk'));
}

/** The tests («контрольні») of a class on a day, for the load check before a third one. */
export function useClassTests(classId: string | undefined, date: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['class-tests', classId, date],
    enabled: classId !== undefined && date !== undefined,
    queryFn: async () =>
      must(
        await db
          .from('lessons')
          .select(
            'id, period, course:courses!lessons_school_id_course_id_fkey!inner(class_id, subject:subjects!courses_school_id_subject_id_fkey(name))',
          )
          .eq('date', date as string)
          .eq('is_test', true)
          .eq('course.class_id', classId as string),
      ).map((l) => ({ id: l.id, period: l.period, subject: l.course.subject.name })),
  });
}

/** A teacher plans a lesson: its topic, what to bring, whether it is a test. */
export function useLessonPlan() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { lessonId: string; topic: string; bring: string; isTest: boolean }) =>
      must(
        await db
          .from('lessons')
          .update({
            topic: a.topic.trim() || null,
            bring: a.bring.trim() || null,
            is_test: a.isTest,
          })
          .eq('id', a.lessonId),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['lessons'] });
      void client.invalidateQueries({ queryKey: ['class-tests'] });
    },
  });
}
